import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { pb, User, logout as pbLogout, isUser } from './pocketbase';
import { checkSession, AuthSession } from './auth-pb';
import { courseEntitlementApi } from './api-pb';

type SubscriptionTier = 'free' | 'student' | 'basic' | 'full' | 'creator' | 'top_creator';

interface AuthContextType {
  user: User | null;
  subscriptionTier: SubscriptionTier;
  hasCourseGrant: boolean;
  /** Start of the student allowance, or null without an active course link. */
  courseGrantedAt: string | null;
  refreshCourseEntitlement: () => Promise<void>;
  /**
   * Аккаунт без лимитов тарифа (`UNLIMITED_USER_IDS` на бэкенде).
   * Тариф при этом остаётся настоящим — интерфейс не должен показывать
   * пользователю план, которого он не покупал.
   */
  isUnlimited: boolean;
  /**
   * Единственный признак «платные возможности закрыты».
   *
   * Раньше каждая страница считала его сама как `subscriptionTier === 'free'`,
   * и безлимитные аккаунты упирались в пейволл: `UNLIMITED_USER_IDS` снимал
   * лимиты только на бэкенде, а UI об этом не знал.
   */
  isFree: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (session: AuthSession) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [courseGrant, setCourseGrant] = useState<string | null>(null);
  const refreshCourseEntitlement = useCallback(async () => {
    try { const status = await courseEntitlementApi.status(); setCourseGrant(status.active ? status.grantedAt : null); }
    catch { setCourseGrant(null); }
  }, []);

  useEffect(() => {
    if (!user) { setCourseGrant(null); return; }
    void refreshCourseEntitlement();
  }, [user?.id, refreshCourseEntitlement]);

  // Обновление данных пользователя с сервера
  const refreshUser = useCallback(async () => {
    try {
      const authData = await pb.collection('users').authRefresh();
      if (isUser(authData.record)) {
        setUser(authData.record);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      pbLogout();
      setUser(null);
    }
  }, []);

  // Инициализация при загрузке
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const session = await checkSession();
        if (session) {
          setUser(session.user);
        }
      } catch (error) {
        console.error('Auth init error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Подписываемся на изменения auth store
    const unsubscribe = pb.authStore.onChange((_token, model) => {
      if (model && isUser(model)) {
        setUser(model);
      } else {
        setUser(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Вход (вызывается после успешного login/signup)
  const login = useCallback((session: AuthSession) => {
    setUser(session.user);
  }, []);

  // Выход
  const logout = useCallback(() => {
    pbLogout();
    setUser(null);
  }, []);

  // Локальное обновление данных пользователя (без запроса к серверу)
  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  // Проверка активности подписки
  const subscriptionTier = useMemo((): SubscriptionTier => {
    if (!user) return 'free';

    const tier = (user.subscription_tier || 'free') as SubscriptionTier;
    if (tier === 'free') return courseGrant ? 'student' : 'free';

    // Все платные тарифы (basic/full/creator/top_creator) — доступ по сроку.
    // Нет end_date → нет доступа.
    if (!user.subscription_end_date) return courseGrant ? 'student' : 'free';

    const endDate = new Date(user.subscription_end_date);
    if (endDate < new Date()) return courseGrant ? 'student' : 'free';

    return tier;
  }, [user, courseGrant]);

  const isUnlimited = user?.unlimited === true;

  const value = useMemo(
    () => ({
      user,
      subscriptionTier,
      hasCourseGrant: !!courseGrant,
      courseGrantedAt: courseGrant,
      refreshCourseEntitlement,
      isUnlimited,
      isFree: subscriptionTier === 'free' && !isUnlimited,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
      updateUser,
    }),
    [user, subscriptionTier, courseGrant, refreshCourseEntitlement, isUnlimited, isLoading, login, logout, refreshUser, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Хук для доступа к контексту аутентификации
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/**
 * Хук для получения текущего пользователя (с проверкой авторизации)
 */
export function useCurrentUser(): User {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) {
    throw new Error('User is not authenticated');
  }
  return user;
}

/**
 * Хук для проверки уровня подписки
 */
export function useSubscription() {
  const { subscriptionTier, user, isUnlimited, isFree } = useAuth();

  // Безлимитный аккаунт считается платным для любых проверок доступа, но
  // `tier` ниже отдаётся настоящий: страница тарифов, бейдж в шапке и боковое
  // меню показывают план, а не право доступа.
  const isPremium = !isFree;
  const isBasic = subscriptionTier === 'basic' && !isUnlimited;
  const isFull = subscriptionTier === 'full' && !isUnlimited;
  const isOneTime = isBasic || isFull;
  const isTopCreator = subscriptionTier === 'top_creator';

  const daysRemaining = useMemo(() => {
    if (!user?.subscription_end_date) return 0;
    const endDate = new Date(user.subscription_end_date);
    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [user?.subscription_end_date]);

  return {
    tier: subscriptionTier,
    isUnlimited,
    isFree,
    isPremium,
    isBasic,
    isFull,
    isOneTime,
    isTopCreator,
    daysRemaining,
    endDate: user?.subscription_end_date,
    billingPeriod: user?.subscription_billing_period,
  };
}
